import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Lightbulb, ThumbsUp, ThumbsDown, Trash, Clock } from "@phosphor-icons/react";

export default function CompanySuggestions() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ title: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setList((await api.get("/suggestions")).data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Título obrigatório");
    if (!form.description.trim()) return toast.error("Descreva sua sugestão");
    setSaving(true);
    try {
      await api.post("/suggestions", form);
      toast.success("Sugestão enviada! Obrigado por contribuir 💡");
      setForm({ title: "", description: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover esta sugestão?")) return;
    try { await api.delete(`/suggestions/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-8 max-w-4xl">
      <div className="mb-8">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Comunidade</p>
        <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A] mt-1 flex items-center gap-3">
          <Lightbulb size={36} weight="duotone" className="text-[#F59E0B]" />
          Sua sugestão importa
        </h1>
        <p className="text-sm text-[#5C5C59] mt-2 max-w-2xl">
          O que a gente pode melhorar? Que funcionalidade tá faltando na plataforma pra ficar
          perfeita pro seu dia a dia? Manda sua ideia — o administrador vai avaliar pessoalmente
          e você recebe uma resposta 👍 ou 👎 aqui na página.
        </p>
      </div>

      {/* Submission form */}
      <div className="bg-white border border-[#E5E5E0] shadow-brutal p-6 mb-8">
        <div className="space-y-4">
          <div>
            <Label className="text-sm">Título da sugestão</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="sugg-title-input"
              className="rounded-none mt-1"
              placeholder="Ex: Botão para exportar contatos em CSV"
              maxLength={120}
            />
          </div>
          <div>
            <Label className="text-sm">Descrição completa</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="sugg-desc-input"
              className="rounded-none mt-1 min-h-[120px]"
              placeholder="Descreva o problema que a sugestão resolve, como você imagina que funcionaria e por que seria útil pra outras empresas também."
              maxLength={2000}
            />
            <p className="text-[10px] text-[#5C5C59] mt-1">
              💡 Dica: quanto mais detalhado, maior a chance da sugestão ser priorizada.
            </p>
          </div>
          <div className="flex justify-end">
            <Button onClick={submit} disabled={saving} data-testid="sugg-submit-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none shadow-brutal">
              <Lightbulb size={16} weight="bold" className="mr-1.5" />
              {saving ? "Enviando…" : "Enviar sugestão"}
            </Button>
          </div>
        </div>
      </div>

      {/* My suggestions */}
      <h2 className="text-lg font-black tracking-tighter text-[#1A1A1A] mb-3">Minhas sugestões</h2>
      {list.length === 0 && (
        <div className="bg-white border border-dashed border-[#E5E5E0] p-8 text-center text-sm text-[#5C5C59]">
          Você ainda não enviou nenhuma sugestão. Manda a primeira aí em cima 👆
        </div>
      )}
      <div className="space-y-3" data-testid="sugg-list">
        {list.map((s) => (
          <div key={s.id} className="bg-white border border-[#E5E5E0] shadow-brutal p-5" data-testid={`sugg-row-${s.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="font-bold text-sm">{s.title}</div>
                <p className="text-xs text-[#5C5C59] mt-1 whitespace-pre-wrap">{s.description}</p>
                <div className="text-[10px] text-[#5C5C59] mt-2 flex items-center gap-1">
                  <Clock size={11} weight="bold" /> Enviada em {new Date(s.created_at).toLocaleString("pt-BR")}
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                {s.admin_vote === "up" && (
                  <div className="flex flex-col items-center bg-[#DCFCE7] text-[#166534] border border-[#86EFAC] px-3 py-2">
                    <ThumbsUp size={20} weight="fill" />
                    <div className="text-[9px] uppercase tracking-widest font-bold mt-1">Aprovada</div>
                  </div>
                )}
                {s.admin_vote === "down" && (
                  <div className="flex flex-col items-center bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5] px-3 py-2">
                    <ThumbsDown size={20} weight="fill" />
                    <div className="text-[9px] uppercase tracking-widest font-bold mt-1">Não agora</div>
                  </div>
                )}
                {!s.admin_vote && (
                  <div className="flex flex-col items-center bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] px-3 py-2">
                    <Clock size={20} weight="fill" />
                    <div className="text-[9px] uppercase tracking-widest font-bold mt-1">Em análise</div>
                  </div>
                )}
                <button onClick={() => remove(s.id)} className="text-[#DC2626] hover:opacity-70" title="Remover">
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            </div>
            {s.admin_comment && (
              <div className="mt-3 pt-3 border-t border-[#E5E5E0] text-xs">
                <span className="font-bold text-[#0F4C3A]">Resposta do admin:</span>{" "}
                <span className="text-[#1A1A1A]">{s.admin_comment}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
